import { useEffect, useRef, useState } from 'react';
import WebViewer from '@pdftron/pdfjs-express';
import { base64StringToBlob, generatePdfTools } from '../helpers';

export const usePDFExpressWebViewer = ({
  files,
  handleDocSave,
  handleDocDelete,
  isReadOnly = false
}: {
  files: any[];
  handleDocSave?: (file: any, index: number) => Promise<void>;
  handleDocDelete?: (file: any, index: number) => Promise<void>;
  isReadOnly?: boolean;
}) => {
  const [isLoadingWebViewer, setIsLoadingWebView] = useState<boolean>(true);
  const [unsavedChanges, setUnsavedChanges] = useState<boolean>(false);
  const viewerRefs = useRef<any[]>([]);

  const handleAnnotationChange = (annotations, action) => {
    if (action === 'add' || action === 'modify' || action === 'delete') {
      setUnsavedChanges(true);
    }
  };

  useEffect(() => {
    if (isLoadingWebViewer || (files && files.length > 0)) {
      files?.forEach(async (file, index) => {
        try {
          const instance = await WebViewer(
            {
              path: '/webviewer/lib',
              enabledElements: ['downloadButton'],
              ...(isReadOnly && {
                disabledElements: [
                  'toolsButton',
                  'searchButton',
                  'menuButton',
                  'annotationToolGroupButton',
                  'textToolGroupButton',
                  'shapeToolGroupButton',
                  'rubberStampToolGroupButton',
                  'pencilToolGroupButton',
                  'eraserToolButton'
                ]
              })
            },
            viewerRefs?.current?.[index]
          );
          if (instance) {
            viewerRefs.current[index].instance = instance;
            const { annotationManager } = generatePdfTools(instance);
            if (handleDocSave && !isReadOnly) {
              instance.UI.setHeaderItems(header => {
                header.push({
                  title: 'Save',
                  type: 'actionButton',
                  img: '<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24"><path d="M0 0h24v24H0z" fill="none"/><path d="M17 3H5c-1.11 0-2 .9-2 2v14c0 1.1.89 2 2 2h14c1.1 0 2-.9 2-2V7l-4-4zm-5 16c-1.66 0-3-1.34-3-3s1.34-3 3-3 3 1.34 3 3-1.34 3-3 3zm3-10H5V5h10v4z"/></svg>',
                  onClick: () => handleDocSave(file, index)
                });
              });
            }
            if (handleDocDelete && file?.fieldWalkthroughItemFileId && !isReadOnly) {
              instance.UI.disableElements(['searchButton']);
              instance.UI.setHeaderItems(header => {
                header.push({
                  title: 'Delete',
                  type: 'actionButton',
                  img: '<svg xmlns="http://www.w3.org/2000/svg" height="24" viewBox="0 0 24 24" width="24"><path d="M0 0h24v24H0z" fill="none"/><path d="M6 19c0 1.1.9 2 2 2h8c1.1 0 2-.9 2-2V7H6v12zM19 4h-3.5l-1-1h-5l-1 1H5v2h14V4z"/></svg>',
                  onClick: () => handleDocDelete(file, index)
                });
              });
            }

            annotationManager?.addEventListener('annotationChanged', (annotations, action) => {
              handleAnnotationChange(annotations, action);
            });
            if (file) {
              let blob;
              let url;
              if (file.fileContents) {
                blob = base64StringToBlob(file.fileContents);
                url = URL.createObjectURL(blob);
              }

              // Load the document first
              await instance.UI.loadDocument(
                file?.storageUri ? `${file.storageUri}?date=${new Date().toISOString()}` : url,
                {
                  extension: 'pdf',
                  filename: `${file.description}.pdf`
                }
              );

              // Wait for document to be fully loaded, then import XML annotation data
              if (file.xmlAnnotationData && file.xmlAnnotationData.trim()) {
                instance.Core.documentViewer.addEventListener('documentLoaded', async () => {
                  try {
                    await annotationManager.importAnnotations(file.xmlAnnotationData);
                  } catch (xmlError) {
                    console.error('Error loading XML annotation data:', xmlError);
                  }
                });
              }
            }
          }
        } catch (error) {
          console.log(error);
        } finally {
          setIsLoadingWebView(false);
        }
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isLoadingWebViewer, files]);
  return {
    viewerRefs,
    setIsLoadingWebView,
    isLoadingWebViewer,
    unsavedChanges,
    setUnsavedChanges
  };
};
